import {
  CONSENT_COOKIE_NAME,
  CONSENT_MAX_AGE_SECONDS,
  CONSENT_POLICY_VERSION,
  CONSENT_REGION_MAX_AGE_SECONDS,
  ConsentCookieSchema,
  isConsentCurrent,
  type ConsentBasis,
  type ConsentCookie,
} from "@cede/types";
import { randomUuid } from "@cede/utils";

/**
 * The consent decision lives in a cookie, not a zustand `persist` store.
 *
 * A cookie is the only store every `cedehub.io` host can read, so a decision
 * made on one Hub subdomain travels to the next instead of being asked again.
 * Arena is a different registrable domain and keeps its own decision.
 */

const HUB_COOKIE_DOMAIN = ".cedehub.io";

/** Only where the sibling Hub hosts exist to share the decision with. */
function sharesHubDomain(): boolean {
  const { hostname } = window.location;
  return hostname === "cedehub.io" || hostname.endsWith(HUB_COOKIE_DOMAIN);
}

function cookieAttributes(maxAgeSeconds: number): string {
  const parts = [`Path=/`, `Max-Age=${maxAgeSeconds}`, `SameSite=Lax`];
  if (sharesHubDomain()) parts.push(`Domain=${HUB_COOKIE_DOMAIN}`);
  // `Secure` on plain http silently drops the write, which would loop the banner locally.
  if (window.location.protocol === "https:") parts.push("Secure");
  return parts.join("; ");
}

function rawCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1] ?? null;
}

/**
 * The stored decision, or null when there is none we can act on.
 *
 * Anything unparseable, hand-edited or written against a superseded policy
 * version reads as absent, which shows the banner again. Guessing at a damaged
 * value would mean loading tags on a consent we cannot evidence.
 */
export function readConsentCookie(): ConsentCookie | null {
  const raw = rawCookie(CONSENT_COOKIE_NAME);
  if (raw == null) return null;

  try {
    const parsed = ConsentCookieSchema.safeParse(JSON.parse(decodeURIComponent(raw)));
    if (!parsed.success) return null;
    return isConsentCurrent(parsed.data) ? parsed.data : null;
  } catch {
    return null;
  }
}

export function writeConsentCookie(consent: ConsentCookie): void {
  const maxAge = consent.basis === "region" ? CONSENT_REGION_MAX_AGE_SECONDS : CONSENT_MAX_AGE_SECONDS;
  const value = encodeURIComponent(JSON.stringify(consent));
  document.cookie = `${CONSENT_COOKIE_NAME}=${value}; ${cookieAttributes(maxAge)}`;
}

export function buildConsent({ analytics, basis }: { analytics: boolean; basis: ConsentBasis }): ConsentCookie {
  return {
    analytics,
    basis,
    pv: CONSENT_POLICY_VERSION,
    id: randomUuid(),
    synced: false,
  };
}

/**
 * Cookies written by the vendors we gate, so a refusal actually removes what a
 * previous visit left behind. Hub runs no Meta Pixel, hence no `_fb*`.
 *
 * Everyone who used Hub before the banner shipped is carrying these already;
 * without this an auditor finds `_ga` on a session that reads as rejected.
 */
// Identical in all three apps on purpose: marketing can add a vendor through the
// GTM UI without a PR, so Hub sweeps `_fbp`/`_fbc` even with no Pixel of its own.
const VENDOR_COOKIE_PREFIXES = ["_ga", "_gid", "_gcl", "_gac", "_fbp", "_fbc", "mp_", "_clck", "_clsk"];

export function clearVendorCookies(): void {
  const { hostname } = window.location;
  const domains = sharesHubDomain() ? [hostname, HUB_COOKIE_DOMAIN] : [hostname];

  for (const entry of document.cookie.split("; ")) {
    const name = entry.split("=")[0];
    if (!name || !VENDOR_COOKIE_PREFIXES.some((prefix) => name.startsWith(prefix))) continue;

    for (const domain of domains) {
      document.cookie = `${name}=; Path=/; Domain=${domain}; Max-Age=0`;
    }
    // Host-only cookies ignore a Domain attribute, so they need their own delete.
    document.cookie = `${name}=; Path=/; Max-Age=0`;
  }
}
